import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ItemList from '../../../Common/ItemList/ItemList'
import { Button, Grid } from '@mui/material'
import { AddRounded as AddRoundedIcon } from '@mui/icons-material'
import EditAddressDialog from './EditAddressDialog'
import Address from './Address'
import AddressSkeleton from './AddressSkeleton'
import { type UserAddressStore } from '../../../UserData/_stores/UserAddressStore'
import AccountHeader from '../../AccountHeader'
import AccountEmptyCard from '../../AccountEmptyCard'
import PaginationItemListStore from '../../../UserData/_stores/PaginationItemListStore'

const LIMIT = 6

export default function AddressList() {
  const [t] = useTranslation('account-v2')

  const [count, setCount] = useState(0)
  const [addDialogOpened, setAddDialogOpened] = useState(false)

  const loadingGrid = useMemo(() => {
    const listSkeletons = []

    for (let i = 0; i < LIMIT; i++) {
      listSkeletons.push(<AddressSkeleton key={i} />)
    }

    return listSkeletons
  }, [])

  const onAddOpen = useCallback(() => {
    setAddDialogOpened(true)
  }, [])

  const onAddClose = useCallback(() => {
    setAddDialogOpened(false)
  }, [])

  const renderAddress = useCallback((item: UserAddressStore) => {
    return <Address address={item} />
  }, [])

  const renderList = useCallback((items: JSX.Element | null, links: JSX.Element | null, loading: boolean) => {
    return <>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {loading
          ? loadingGrid
          : (items ?? <Grid item xs={12} md={6} lg={4}>
            <AccountEmptyCard
              title={t('No addresses yet')}
              description={t('Save an address to speed up checkout and store setup.')}
              actionLabel={t('Add address')}
              onAction={onAddOpen}
            />
          </Grid>)
        }
      </Grid>

      {links}
    </>
  }, [loadingGrid, onAddOpen, t])

  const onUpdate = useCallback((itemList: PaginationItemListStore|undefined) => {
    if (itemList) {
      setCount(itemList.get('total'))
    }
  }, [])

  return <div>
    <AccountHeader
      title={t('Addresses')}
      actions={count > 0 ? <Button
        variant="contained"
        color="accent"
        startIcon={<AddRoundedIcon />}
        onClick={onAddOpen}
      >{t('Add address')}</Button> : null}
    />
    <ItemList
      dataType="addresses"
      item={renderAddress}
      limit={LIMIT}
      render={renderList}
      onUpdate={onUpdate}
    />
    <EditAddressDialog
      opened={addDialogOpened}
      onClose={onAddClose}
    />
  </div>
}
